import "../utils/polyfills/stringReplaceAll";
import "../sentry.client.config";
import "../styles/globals.css";
import "nprogress/nprogress.css";
import "../styles/font/golostext.css";
import "../styles/font/gilroy.css";
import "../styles/font/manrope.css";

import Router, { useRouter } from "next/router";
import NProgress from "nprogress";
import { useEffect, useRef } from "react";

import ErrorBoundary from "~/components/common/ErrorBoundary";
import { useInterceptorBridge } from "~/hooks/useInterceptorBridge";
import { usePageMetaTracking } from "~/hooks/usePageMetaTracking";
import { SITE_PAGE_TYPE } from "~/pagesSrrLogic/sitePageType";
import errorSent from "~/utils/errors/errorSent";
import { initInterceptor } from "~/utils/interceptorRuntime";

import { wrapper } from "../redux";

Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

// Просмотр при загрузке страницы засчитывает сам init счётчика
// (include/scripts.html в Bitrix), вручную шлём только переходы внутри сайта.
// Сравнение с прошлым адресом отсекает replace, который Next делает
// после гидратации на том же URL.
let lastHitUrl =
  typeof window !== "undefined"
    ? window.location.pathname + window.location.search + window.location.hash
    : "";
Router.events.on("routeChangeComplete", (url: string) => {
  if (url === lastHitUrl) {
    return;
  }
  lastHitUrl = url;
  // @ts-ignore
  if (typeof ym !== "undefined") {
    // @ts-ignore
    ym(20518312, "hit", url);
  }
});

function normalizeErrorCode(value: unknown) {
  const text =
    typeof value === "string" ? value : String(value ?? "notDetected");
  return text
    .toLowerCase()
    .replace(/[^a-zA-Zа-яА-ЯёЁ\s]/g, "")
    .replace(/ /g, "_");
}

function MyApp({ Component, pageProps }: any) {
  const router = useRouter();
  const onInterceptorTrigger = useInterceptorBridge();
  // Реальная мета страницы уходит в мониторинг панели
  usePageMetaTracking();
  // Окна перехватчика не открываются сами на карточке товара: закрывают блок покупки.
  const pageTypeRef = useRef(pageProps?.pageType);
  pageTypeRef.current = pageProps?.pageType;

  useEffect(() => {
    const onError = (event: any) => {
      try {
        errorSent(
          "errorUnhandled",
          normalizeErrorCode(event?.message),
          {},
          event,
        );
      } catch (e) {
        console.warn("Sent error error", e);
      }
    };

    const onUnhandled = (event: any) => {
      try {
        const { reason } = event;
        let message: unknown;

        if (reason instanceof Error) {
          message = reason.message;
        } else if (typeof reason === "string") {
          message = reason;
        } else if (typeof reason === "object") {
          message = JSON.stringify(reason);
        } else {
          message = "notDetected";
        }

        errorSent(
          "errorUnhandledRejection",
          normalizeErrorCode(message),
          {},
          event,
        );
      } catch (e) {
        console.warn("Sent error error", e);
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandled);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandled);
    };
  }, []);

  useEffect(() => {
    if (!router.isReady || typeof window === "undefined") {
      return;
    }
    if (router.asPath.includes("personal")) {
      return;
    }
    const { handleRouteChange, destroy } = initInterceptor({
      onTrigger: (payload: any) =>
        pageTypeRef.current !== SITE_PAGE_TYPE.product &&
        onInterceptorTrigger(payload),
    });

    handleRouteChange(router.asPath);
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      destroy();
    };
  }, [router.isReady, router.events, onInterceptorTrigger]);

  return (
    <ErrorBoundary key={router.asPath}>
      <Component {...pageProps} />
    </ErrorBoundary>
  );
}

export default wrapper.withRedux(MyApp);
